import React, { useState, useCallback, useMemo } from 'react';
import { Mic, Play, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { MediaInput } from './MediaInput';
import { TranscriptionResult } from './TranscriptionResult';
import { LoginModal } from '../auth';
import { LLMModelSelector } from '@/components/selectors';
import { CompactSelect } from '@/components/common/CompactSelect';
import { TokenEstimateCard } from '@/components/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { LLM_PROVIDERS, DEFAULT_VALUES } from '@/constants';
import { useLLMModels } from '@/hooks/useLLMModels';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { KB } from '@/constants/fileSize';
import { COST_GATE_OPEN, type CostGateStatus, type CostGate } from '../../utils/costGate';

// Bytes per second estimate for audio at ~128kbps
const BYTES_PER_SECOND_AUDIO_ESTIMATE = 16 * KB;

export const SpeechToTextTab: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();

  // File and UI state (not tied to the async job)
  const [file, setFile] = useState<File | null>(null);
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  // AI Enhancement state
  const [aiEnhanced, setAiEnhanced] = useState<boolean>(false);
  const [llmProvider, setLlmProvider] = useState<string>(DEFAULT_VALUES.LLM_PROVIDER);
  const {
    availableModels,
    isLoadingModels,
    selectedModel: llmModel,
    setSelectedModel: setLlmModel,
  } = useLLMModels(llmProvider, undefined, { enabled: aiEnhanced });
  const [showModelSelector, setShowModelSelector] = useState<boolean>(false);

  // Async transcription lifecycle — delegated to GenerationContext via hook.
  const {
    isTranscribing,
    uploadProgress,
    stageMessage,
    result,
    error: transcriptionError,
    startTranscription,
    reset: resetTranscription,
  } = useSpeechToText();

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    resetTranscription();
  }, [resetTranscription]);

  const handleStartTranscription = useCallback(async () => {
    if (!file) {
      showToast(
        'error',
        t('stt.errors.noFileTitle', { defaultValue: 'No File Selected' }),
        t('stt.errors.noFileMessage', {
          defaultValue: 'Please upload a video or audio file first.',
        })
      );
      return;
    }

    requireAuth(async () => {
      const aiEnhancementOptions = aiEnhanced
        ? {
            enabled: true as const,
            llmProvider,
            llmModel: llmModel || undefined,
          }
        : undefined;

      await startTranscription(file, aiEnhancementOptions);

      if (result) {
        const successMessage = result.refinementApplied
          ? t('stt.success.messageEnhanced', {
              defaultValue: 'Successfully transcribed and enhanced {{count}} segments',
              count: result.segments.length,
            })
          : t('stt.success.message', {
              defaultValue: 'Successfully transcribed {{count}} segments',
              count: result.segments.length,
            });

        showToast(
          'success',
          t('stt.success.title', { defaultValue: 'Transcription Complete' }),
          successMessage
        );
      }

      if (transcriptionError) {
        showToast(
          'error',
          t('stt.errors.failedTitle', { defaultValue: 'Transcription Failed' }),
          transcriptionError
        );
      }
    }, t('auth.loginRequired.stt'));
  }, [
    file,
    showToast,
    t,
    aiEnhanced,
    llmProvider,
    llmModel,
    requireAuth,
    startTranscription,
    result,
    transcriptionError,
  ]);

  const handleNewTranscription = useCallback(() => {
    setFile(null);
    resetTranscription();
  }, [resetTranscription]);

  const handleToggleAiEnhanced = useCallback((enabled: boolean) => {
    setAiEnhanced(enabled);
    if (enabled) {
      setShowModelSelector(true);
    }
  }, []);

  const estimatedDurationSeconds = useMemo(() => {
    if (!file) return 0;
    const estimatedSeconds = file.size / BYTES_PER_SECOND_AUDIO_ESTIMATE;
    return Math.max(10, Math.min(estimatedSeconds, 7200));
  }, [file]);

  const estimateParams = useMemo(
    () => ({
      duration_seconds: estimatedDurationSeconds,
      model: 'large-v3',
      language_detection: true,
      ai_enhanced: aiEnhanced,
    }),
    [estimatedDurationSeconds, aiEnhanced]
  );

  const handleEstimateChange = useCallback(({ status }: CostGate) => {
    setCostGate(status);
  }, []);

  const llmProviderOptions = LLM_PROVIDERS.map((provider) => ({
    value: provider.value,
    label: provider.label,
    disabled: provider.disabled,
  }));

  return (
    <main className="page-container">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl mb-4">
          <Mic className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
          {t('stt.title', { defaultValue: 'Speech to Text' })}
        </h2>
        <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
          {t('stt.subtitle', {
            defaultValue: 'Transcribe audio and video files with AI-powered speech recognition',
          })}
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Upload Section */}
        {!result && (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Mic className="h-5 w-5" />
              <span>{t('stt.sections.input', { defaultValue: 'Audio Input' })}</span>
            </h3>

            <MediaInput
              onFileSelect={handleFileSelect}
              onRemove={handleRemoveFile}
              disabled={isTranscribing}
              isUploading={isTranscribing}
              uploadProgress={uploadProgress}
            />
          </div>
        )}

        {/* AI Enhancement Section */}
        {!result && file && (
          <div className="card p-6">
            <div className="space-y-4">
              {/* AI Enhanced Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="ai_enhanced"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-white/20 rounded bg-black/20"
                    checked={aiEnhanced}
                    onChange={(e) => handleToggleAiEnhanced(e.target.checked)}
                    disabled={isTranscribing}
                  />
                  <label
                    htmlFor="ai_enhanced"
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    <span className="text-sm font-medium text-white/80">
                      {t('stt.aiEnhanced.label', { defaultValue: 'AI Enhanced' })}
                    </span>
                  </label>
                </div>
                {aiEnhanced && (
                  <button
                    type="button"
                    onClick={() => setShowModelSelector(!showModelSelector)}
                    className="flex items-center space-x-1 text-sm text-white/60 hover:text-white/80 transition-colors"
                  >
                    <span>
                      {t('stt.aiEnhanced.modelSettings', { defaultValue: 'Model Settings' })}
                    </span>
                    {showModelSelector ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>

              {/* AI Enhanced Description */}
              <p className="text-sm text-white/60 ml-7">
                {t('stt.aiEnhanced.description', {
                  defaultValue:
                    'Use AI to improve transcription accuracy by fixing punctuation, grammar, and filler words.',
                })}
              </p>

              {/* Model Selector (Collapsible) */}
              {aiEnhanced && showModelSelector && (
                <div className="mt-4 p-4 bg-black/20 rounded-lg border border-green-500/30 space-y-4">
                  {/* Provider Selector */}
                  <CompactSelect
                    label={t('forms.videoConfig.labels.llmProvider', {
                      defaultValue: 'LLM Provider',
                    })}
                    options={llmProviderOptions}
                    value={llmProvider}
                    onChange={setLlmProvider}
                    disabled={isTranscribing}
                    testId="stt-llm-provider-select"
                  />

                  {/* Visual Model Selector with Avatars */}
                  <LLMModelSelector
                    models={
                      isLoadingModels
                        ? [{ value: '', label: 'Loading models...', disabled: true }]
                        : availableModels.length === 0
                          ? [{ value: '', label: 'No models available', disabled: true }]
                          : availableModels
                    }
                    value={llmModel}
                    onChange={setLlmModel}
                    disabled={isTranscribing || isLoadingModels || availableModels.length === 0}
                    label={t('forms.videoConfig.labels.llmModel', { defaultValue: 'AI Model' })}
                    showFreeOnly={false}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Token Estimate */}
        {!result && file && estimatedDurationSeconds > 0 && (
          <TokenEstimateCard
            operationPath="/api/v1/transcription/upload"
            parameters={estimateParams}
            onEstimateChange={handleEstimateChange}
          />
        )}

        {/* Start Button */}
        {!result && file && (
          <div className="flex justify-center">
            <button
              onClick={handleStartTranscription}
              disabled={isTranscribing || !file || costGate !== 'affordable'}
              className={`
                flex items-center space-x-2 px-8 py-3 rounded-lg font-semibold text-white
                transition-all duration-200
                ${
                  isTranscribing || !file || costGate !== 'affordable'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {isTranscribing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('stt.buttons.transcribing', { defaultValue: 'Transcribing...' })}</span>
                </>
              ) : (
                <>
                  {aiEnhanced ? <Sparkles className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  <span>
                    {aiEnhanced
                      ? t('stt.buttons.startEnhancedTranscription', {
                          defaultValue: 'Start AI-Enhanced Transcription',
                        })
                      : t('stt.buttons.startTranscription', {
                          defaultValue: 'Start Transcription',
                        })}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Progress indicator during transcription */}
        {isTranscribing && (
          <div className="card p-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
              <div className="text-center w-full max-w-sm">
                <p className="text-lg font-medium text-white/70 mb-3">
                  {stageMessage ??
                    (uploadProgress < 50
                      ? t('stt.progress.uploading', { defaultValue: 'Uploading file...' })
                      : aiEnhanced
                        ? t('stt.progress.processingEnhanced', {
                            defaultValue: 'Processing audio with WhisperX and AI enhancement...',
                          })
                        : t('stt.progress.processing', {
                            defaultValue: 'Processing audio with WhisperX...',
                          }))}
                </p>
                {/* Progress bar */}
                <div className="w-full bg-black/30 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-green-600 to-teal-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, uploadProgress))}%` }}
                  />
                </div>
                <p className="text-sm text-white/40 mt-2">
                  {uploadProgress < 100
                    ? `${Math.round(uploadProgress)}%`
                    : t('stt.progress.hint', {
                        defaultValue: 'This may take a few moments depending on the file length',
                      })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <>
            {/* AI Enhancement Badge */}
            {result.refinementApplied && (
              <div className="flex items-center justify-center space-x-2 py-2 px-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                <Sparkles className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-amber-300">
                  {t('stt.results.enhancedBy', {
                    defaultValue: 'Enhanced by AI',
                    model: result.modelUsed || 'AI',
                  })}
                  {result.modelUsed && (
                    <span className="font-medium ml-1">({result.modelUsed})</span>
                  )}
                </span>
              </div>
            )}

            <TranscriptionResult
              segments={result.segments}
              fullText={result.fullText}
              detectedLanguage={result.detectedLanguage}
              processingTime={result.processingTime}
            />

            {/* New Transcription Button */}
            <div className="flex justify-center">
              <button
                onClick={handleNewTranscription}
                className="flex items-center space-x-2 px-6 py-2.5 bg-black/30 border border-white/10 text-white/70 rounded-lg hover:bg-black/40 transition-colors"
              >
                <Mic className="w-5 h-5" />
                <span>
                  {t('stt.buttons.newTranscription', { defaultValue: 'Transcribe Another File' })}
                </span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Login Modal (auth required for actions) */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </main>
  );
};
